/**
 * ImageUpload — reusable, controlled image upload field (MUI).
 *
 * Drop-in wherever an entity needs a single image (Area, MachinePartType, result photo).
 * Implements Option A: pick file → ask backend for a presigned PUT → upload straight to S3 →
 * report the stored object key back via `onChange`. The parent persists that key on the entity
 * (e.g. Area.imageUrl) when it saves — the file exists in S3 before the entity row does, so this
 * works even when the entity has no id yet.
 *
 * Controlled component:
 *   value     — the currently stored object key (or absolute url), or undefined/empty when none.
 *   onChange  — called with the new object key after a successful upload, or '' when removed.
 *
 * Rendering: a stored key is resolved to a short-lived presigned GET url via presignView.
 * An absolute http(s) value is shown as-is (back-compat with any legacy pasted urls).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { useTheme } from '@mui/material/styles';
import { useNotification } from '../../providers/NotificationProvider';
import {
  presignUpload,
  presignView,
  uploadToS3,
  type ImagePurpose,
} from '../../api/imageApi';

export interface ImageUploadProps {
  /** Stored object key (or absolute url for legacy values). */
  value?: string;
  /** Called with the new object key on upload success, or '' when the image is removed. */
  onChange: (objectKey: string) => void;
  /** Chooses the S3 key prefix on the backend. */
  purpose: ImagePurpose;
  /** Field label shown above the drop zone. */
  label?: string;
  /** Helper text under the drop zone. */
  helperText?: string;
  /** Accepted mime types. Default: common image types. */
  accept?: string;
  /** Max file size in MB. Default: 10. */
  maxSizeMb?: number;
  /** Disable interaction. */
  disabled?: boolean;
  /** Preview box height in px. Default: 160. */
  previewHeight?: number;
}

const isAbsoluteUrl = (v: string) => /^https?:\/\//i.test(v);

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  purpose,
  label = 'Image',
  helperText,
  accept = 'image/png,image/jpeg,image/jpg,image/webp,image/gif',
  maxSizeMb = 10,
  disabled = false,
  previewHeight = 160,
}) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const inputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  // Resolved display url for the current value (presigned GET or the absolute url itself).
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [resolvingView, setResolvingView] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  // Resolve `value` → a renderable url whenever it changes.
  useEffect(() => {
    let cancelled = false;
    setPreviewError(false);

    if (!value) {
      setDisplayUrl(null);
      return;
    }
    if (isAbsoluteUrl(value)) {
      setDisplayUrl(value);
      return;
    }

    // Stored object key → presign a GET url.
    setResolvingView(true);
    presignView(value)
      .then((res) => {
        if (!cancelled) setDisplayUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) {
          setDisplayUrl(null);
          setPreviewError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setResolvingView(false);
      });

    return () => {
      cancelled = true;
    };
  }, [value]);

  const validate = useCallback(
    (file: File): string | null => {
      const acceptList = accept.split(',').map((s) => s.trim());
      if (acceptList.length > 0 && !acceptList.includes(file.type)) {
        return `Unsupported file type: ${file.type || 'unknown'}`;
      }
      if (file.size > maxSizeMb * 1024 * 1024) {
        return `File is too large (max ${maxSizeMb} MB)`;
      }
      return null;
    },
    [accept, maxSizeMb],
  );

  const handleFile = useCallback(
    async (file: File) => {
      const validationError = validate(file);
      if (validationError) {
        showError(validationError);
        return;
      }

      setUploading(true);
      setProgress(0);
      try {
        const { objectKey, uploadUrl } = await presignUpload(file.type, purpose);
        await uploadToS3(uploadUrl, file, setProgress);
        onChange(objectKey);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [validate, showError, purpose, onChange],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
      // Reset so selecting the same file again re-triggers change.
      e.target.value = '';
    },
    [handleFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      if (disabled || uploading) return;
      const file = e.dataTransfer.files?.[0];
      if (file) void handleFile(file);
    },
    [disabled, uploading, handleFile],
  );

  const openPicker = useCallback(() => {
    if (!disabled && !uploading) inputRef.current?.click();
  }, [disabled, uploading]);

  const handleRemove = useCallback(() => {
    onChange('');
    setDisplayUrl(null);
    setPreviewError(false);
  }, [onChange]);

  const hasImage = !!value;

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={onInputChange}
        disabled={disabled}
      />

      {/* Preview */}
      {hasImage && (
        <Box
          sx={{
            position: 'relative',
            mb: 1,
            borderRadius: 1,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            height: previewHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.action.hover,
          }}
        >
          {resolvingView && <CircularProgress size={24} />}
          {!resolvingView && displayUrl && (
            <Box
              component="img"
              src={displayUrl}
              alt={label}
              onError={() => setPreviewError(true)}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
          {!resolvingView && (previewError || !displayUrl) && (
            <Stack alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
              <BrokenImageIcon />
              <Typography variant="caption">Preview unavailable</Typography>
            </Stack>
          )}
          {!disabled && (
            <IconButton
              size="small"
              onClick={handleRemove}
              disabled={uploading}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'background.paper' },
              }}
              aria-label="Remove image"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* Drop zone / picker */}
      {!hasImage && (
        <Box
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !uploading) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          aria-label={`Upload ${label}`}
          sx={{
            border: `1px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            cursor: disabled || uploading ? 'default' : 'pointer',
            bgcolor: dragActive ? theme.palette.action.hover : 'transparent',
            opacity: disabled ? 0.5 : 1,
            transition: 'border-color 120ms, background-color 120ms',
            outline: 'none',
            '&:focus-visible': {
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Stack alignItems="center" spacing={1}>
            <CloudUploadIcon color={dragActive ? 'primary' : 'action'} />
            <Typography variant="body2" color="text.secondary">
              {uploading ? 'Uploading…' : 'Click or drag an image here'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
              disabled={disabled || uploading}
            >
              Choose file
            </Button>
          </Stack>
        </Box>
      )}

      {/* Progress */}
      {uploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant={progress > 0 ? 'determinate' : 'indeterminate'}
            value={progress}
          />
        </Box>
      )}

      {/* Replace action when an image is present */}
      {hasImage && !disabled && (
        <Button
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={openPicker}
          disabled={uploading}
          sx={{ mt: 0.5 }}
        >
          Replace image
        </Button>
      )}

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default ImageUpload;
